import { TGlobalContext } from '~/types/app.types';
import { TGlobalContextType } from '~/types/global-context.types';

import { ConfigStore } from './config.store';
import { ToastStore } from './toast.store';
import { MobileStore } from './mobile.store';
import type { BaseSessionStore } from './base-session.store';
import type { CompanyStore } from './company.store';

import { FullstoryService } from '~/services/fullstory/fullstory.service';
import { IAuthorizedAnalyticsService } from '~/services/analytics/analytics.service';
import { PayService } from '~/components/ClientExperience/services/pay.service';

import { BaseSessionCtrl } from '~/controllers/base-session.controller';
import { JStorageService } from '~/services/j-storage.service';
import { EventEmitterController } from '~/controllers/event-emitter.controller';
import { GoogleTagManagerService } from '~/services/google-tag-manager.service';
import { RecaptchaService } from '~/services/recaptcha.service';
import { BaseUserStore } from '~/stores/base-user.store';
import { AccountStore } from '~/stores/account.store';
import { LegacyPayService } from '~/components/ClientExperience/services/legacy-pay.service';
import { INetworkStore } from '~/types/dependency.types';
import { BaseSessionAdapter } from '~/services/adapters/base-session.adapter';
import { ImageService } from '~/services/image.service';
import { S3UploadService } from '~/services/s3-upload.service';
import { IApiService } from '~/services/interfaces/IApiService';
import { GracefulService } from '~/services/graceful.service';

export abstract class BaseStore {
    // We must hold instance of the global context so we can run test in parallel, the base store would be split overtime and won't be needed
    /**
     * @deprecated Please use global context directly, this will be removed as part of the root store separation
     */
    globalContext: TGlobalContext;

    /**
     * @deprecated Please use global context directly, this will be removed as part of the root store separation
     */
    globalContextType: TGlobalContextType;

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get configStore(): ConfigStore {
        return this.globalContext.configStore;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get gracefulService(): GracefulService {
        return this.globalContext.gracefulService;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get accountStore(): AccountStore {
        return this.globalContext.accountStore;
    }
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    abstract get companyStore(): CompanyStore;
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    abstract get userStore(): BaseUserStore;
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    abstract get sessionStore(): BaseSessionStore;
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get toastStore(): ToastStore {
        return this.globalContext.toastStore;
    }

    mobileStore: MobileStore; // Builder Mobile Store
    // so we can override accessor in implementing class

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get fullstoryService(): FullstoryService {
        return this.globalContext.fullstoryService;
    }
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get analyticsService(): IAuthorizedAnalyticsService {
        return this.globalContext.analyticsService;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    // Static class that can be init when needed
    get jStorageService(): JStorageService {
        return this.globalContext.jStorageService;
    }
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    abstract get sessionCtrl(): BaseSessionCtrl;

    googleTagManagerService: GoogleTagManagerService = null; // Only used in signup
    recaptchaService: RecaptchaService = null; // Static class that can be init when needed
    payService: PayService = null;
    legacyPayService: LegacyPayService;
    s3UploadService: S3UploadService = null;
    abstract imageService: ImageService;

    eventEmitterCtrl: EventEmitterController = null; // Only used in builder

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    abstract get apiService(): IApiService;
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get networkStore(): INetworkStore {
        return this.globalContext.networkStore;
    }
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    abstract get sessionService(): BaseSessionAdapter;

    protected constructor(globalContext: TGlobalContext) {
        this.globalContext = globalContext;

        this.googleTagManagerService = new GoogleTagManagerService(
            this.globalContext.analyticsService
        );

        this.recaptchaService = new RecaptchaService({
            configProvider: this.configStore
        });

        this.payService = new PayService({
            globalContext: this.globalContext
        }); // TODO: Need to refactor payService to a service and adapter and this cast won't be needed
        this.legacyPayService = new LegacyPayService({
            globalContext: this.globalContext
        }); // TODO: Need to refactor legacyPayService to a service and adapter and this cast won't be needed

        this.eventEmitterCtrl = new EventEmitterController();

        // TODO: this is specific to the flow builder and probably shouldn't be here
        this.mobileStore = new MobileStore();

        this.s3UploadService = new S3UploadService(this.globalContext);
    }
}
